import type { Snippet } from '@plato/embed/snippets';
import { type KeyboardEvent, useId, useState } from 'react';

export function SnippetTabs(props: { label: string; snippets: readonly Snippet[] }) {
  const [selected, setSelected] = useState(0);
  const baseId = useId();
  const tabId = (index: number) => `${baseId}-tab-${index}`;
  const panelId = (index: number) => `${baseId}-panel-${index}`;
  const current = props.snippets[selected];

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (selected + step + props.snippets.length) % props.snippets.length;
    setSelected(next);
    document.getElementById(tabId(next))?.focus();
  };

  return (
    <div>
      <div role="tablist" aria-label={props.label} className="tabs" onKeyDown={onKeyDown}>
        {props.snippets.map((snippet, index) => (
          <button
            key={snippet.id}
            id={tabId(index)}
            type="button"
            role="tab"
            aria-selected={index === selected}
            aria-controls={panelId(index)}
            tabIndex={index === selected ? 0 : -1}
            onClick={() => setSelected(index)}
          >
            {tabTitle(snippet)}
          </button>
        ))}
      </div>
      {current !== undefined && (
        <div role="tabpanel" id={panelId(selected)} aria-labelledby={tabId(selected)}>
          <p className="hint">{current.title}</p>
          <CodeBlock snippet={current} />
        </div>
      )}
    </div>
  );
}

const TAB_TITLES: Readonly<Record<string, string>> = {
  react: 'React',
  'script-tag': 'Script tag',
  js: 'Plain JavaScript',
  env: 'Secret key',
  express: 'Express',
  nextjs: 'Next.js',
  python: 'Python',
};

function tabTitle(snippet: Snippet): string {
  return TAB_TITLES[snippet.id] ?? snippet.title;
}

export function CodeBlock({ snippet }: { snippet: Snippet }) {
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);
  const copy = () =>
    void navigator.clipboard
      .writeText(snippet.code)
      .then(() => setCopied('yes'))
      .catch(() => setCopied('failed'));
  return (
    <div className="code-block">
      <pre>
        <code data-language={snippet.language}>{snippet.code}</code>
      </pre>
      <button type="button" className="copy" onClick={copy} aria-label={`Copy: ${snippet.title}`}>
        {copied === 'yes' ? 'Copied' : copied === 'failed' ? 'Select and copy' : 'Copy'}
      </button>
    </div>
  );
}
